// Kid PIN modal — parent UI for setting or resetting a kid's PIN (the
// code they tap on the shared-device picker). Calls users.setPin, which
// the server gates to the household owner. A kid who had no PIN becomes
// PIN-gated once one is set here; a kid who forgot theirs gets a new one.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api } from "../../lib/rpc";

interface KidPinModalProps {
  open: boolean;
  kidId: string;
  kidName: string;
  onClose: () => void;
  onSuccess: () => void;
  notify: (msg: string) => void;
}

export function KidPinModal({ open, kidId, kidName, onClose, onSuccess, notify }: KidPinModalProps) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPin("");
    setConfirm("");
    setSaving(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !saving) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, saving]);

  if (!open) return null;

  const valid = /^\d{4,6}$/.test(pin) && pin === confirm;

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      await api.usersSetPin(kidId, pin);
      notify(`PIN updated for ${kidName}`);
      onSuccess();
      onClose();
    } catch (err) {
      notify((err as Error)?.message || "couldn't set PIN");
      setSaving(false);
    }
  };

  return (
    <div className="confirm-bg" onClick={() => { if (!saving) onClose(); }}>
      <div className="add-kid-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={`Set PIN for ${kidName}`}>
        <header className="add-kid-head">
          <h3>Set {kidName}'s PIN</h3>
          <button className="icon-btn" onClick={onClose} title="Close" type="button" disabled={saving}>
            <Icon name="x" size={13} />
          </button>
        </header>

        <p className="add-kid-sub">
          This is the 4-6 digit code {kidName} taps on the family tablet to get into
          their account. Setting a new one takes effect immediately.
        </p>

        <div className="add-kid-body">
          <div className="add-kid-row">
            <label className="add-kid-label">New PIN</label>
            <div className="add-kid-pin-pair">
              <input
                className="add-kid-input add-kid-input-narrow"
                value={pin}
                onInput={(e) => setPin((e.currentTarget as HTMLInputElement).value.replace(/\D/g, "").slice(0, 6))}
                type="password"
                inputMode="numeric"
                pattern="\d*"
                placeholder="4-6 digits"
                autoComplete="new-password"
                autoFocus
              />
              <input
                className="add-kid-input add-kid-input-narrow"
                value={confirm}
                onInput={(e) => setConfirm((e.currentTarget as HTMLInputElement).value.replace(/\D/g, "").slice(0, 6))}
                type="password"
                inputMode="numeric"
                pattern="\d*"
                placeholder="Confirm"
                autoComplete="new-password"
                onKeyDown={(e) => { if (e.key === "Enter") void submit(); }}
              />
            </div>
            {pin && !/^\d{4,6}$/.test(pin) && (
              <div className="add-kid-hint add-kid-hint-warn">PIN must be 4-6 digits</div>
            )}
            {pin && /^\d{4,6}$/.test(pin) && confirm && pin !== confirm && (
              <div className="add-kid-hint add-kid-hint-warn">PINs don't match</div>
            )}
          </div>
        </div>

        <footer className="add-kid-foot">
          <button className="manage-tags-done" type="button" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="manage-tags-add-btn" type="button" onClick={submit} disabled={!valid || saving}>
            {saving ? "Saving…" : "Set PIN"}
          </button>
        </footer>
      </div>
    </div>
  );
}
